import { Injectable, signal } from '@angular/core';
import { DataService } from './DataService';
import { IDataService } from './IDataService';
import { EntityBase } from '../_models/EntityBase';

export interface LookupProfile {
  lookupName: string;
  lookupNamespace?: string;
  visibleFields?: string[];
  additionalProperties?: string[];
  separatorString?: string;
  filterField?: string;
  sortingField?: string;
  filterMethodName?: string;
  args?: Record<string, any>;
}

export interface LookupContext {
  lookupName: string;
  lookupNamespace?: string;
  profileHash: string | null;
  lookupHash: string | null;
  lookupBody: LookupItem[] | null;
  lookupProfile: LookupProfile | null | undefined;
}

export interface LookupItem {
  id: string;
  key: string;
  value: string;
  sortOrder?: string;
  visible: boolean;
  additionalData: Record<string, any>;
  children?: LookupItem[];
  selectedValue?: number;
}

@Injectable({ providedIn: 'root' })
export class LookupService {
  private storageKey = 'matwins-lookups';
  private lookupsCache = new Map<string, LookupContext>();
  static StoragePage?: string = "all";

  // Revision : s'incremente quand un lookup est charge. Les champs deja affiches la lisent
  // pour relire leurs options quand les donnees arrivent apres coup.
  private readonly revisionSignal = signal(0);
  readonly revision = this.revisionSignal.asReadonly();

  constructor() {
    this.loadFromStorage();
  }

  getRequiredLookups(requiredLookups: LookupProfile[]): LookupContext[] {
    return requiredLookups.map(profile => {

      const cached = this.lookupsCache.get(this.buildCacheKey(profile.lookupName, profile.lookupNamespace));

      if (cached) {
        // Retourner le contexte réduit avec les hashs
        return {
          lookupName: cached.lookupName,
          lookupNamespace: cached.lookupNamespace,
          lookupProfile: profile,
          profileHash: cached.profileHash,
          lookupHash: cached.lookupHash,
          lookupBody: [] // Tableau vide pour réduire la taille
        };
      }

      // Premier appel - pas de cache
      return {
        lookupName: profile.lookupName,
        lookupNamespace: profile.lookupNamespace,
        lookupProfile: profile,
        profileHash: null,
        lookupHash: null,
        lookupBody: []
      };
    });
  }

  updateLookups(allLookups: LookupContext[]): void {
    let bodyReceived = false;

    allLookups.forEach(lookup => {
      if (lookup.lookupBody && lookup.lookupBody.length > 0) {
        // Nouveau lookup complet - stocker dans le cache
        this.lookupsCache.set(this.buildCacheKey(lookup.lookupName, lookup.lookupNamespace), { ...lookup });
        bodyReceived = true;
      } else if (lookup.lookupHash) {
        // Mise à jour des hashs seulement
        const existing = this.lookupsCache.get(this.buildCacheKey(lookup.lookupName, lookup.lookupNamespace));
        if (existing) {
          existing.profileHash = lookup.profileHash;
          existing.lookupHash = lookup.lookupHash;
        }
      }
    });

    this.saveToStorage();

    if (bodyReceived) {
      this.revisionSignal.update(version => version + 1);
    }
  }

  getLookupData(lookupName: string, lookupNamespace?: string): LookupItem[] {
    const cached = this.lookupsCache.get(this.buildCacheKey(lookupName, lookupNamespace));
    return cached?.lookupBody || [];
  }

  // Cle unique d'un lookup en cache. Le prefixe StoragePage isole les locataires :
  // deux clients peuvent avoir des valeurs differentes sous le meme nom de lookup.
  private buildCacheKey(lookupName: string, lookupNamespace?: string): string {
    return `${LookupService.StoragePage}-${lookupNamespace ?? ''}${lookupName}`;
  }

  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        const data: LookupContext[] = JSON.parse(stored);
        data.forEach(lookup => {
          // Meme cle qu'a l'ecriture, sinon le cache relu n'est jamais retrouve.
          this.lookupsCache.set(this.buildCacheKey(lookup.lookupName, lookup.lookupNamespace), lookup);
        });
      }
    } catch (error) {
      console.warn('Failed to load lookups from storage', error);
    }
  }

  private saveToStorage(): void {
    try {
      const data = Array.from(this.lookupsCache.values());
      localStorage.setItem(this.storageKey, JSON.stringify(data));
    } catch (error) {
      console.warn('Failed to save lookups to storage', error);
    }
  }

  async getLookupsByNames(dataService: IDataService<EntityBase>, lookupNames: string[]): Promise<void> {
    const lookupProfiles: LookupProfile[] = lookupNames.map(name => ({ lookupName: name }));
    return await this.getLookupsByProfiles(dataService, lookupProfiles);
  }
  async getLookupsByProfiles(dataService: IDataService<EntityBase>, lookupProfiles: LookupProfile[]): Promise<void> {
    await dataService.getLookupsOnly(lookupProfiles);
  }
}
